import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Notifications } from '../../store/slices';
import { moveToProduction } from '../../api';
import { EXTRA_FEATURES_CLIENT_IDS } from '../../constants';
import { AppDialog } from '../ui';
import { dateParam } from '../../lib/orderFilters';
import { GENERIC_ERROR_MESSAGE } from '../../lib/errorMessage';
import { useStyles } from './styles';

const LIMIT_OPTIONS = [
    { label: '50 orders', value: 50 },
    { label: '100 orders', value: 100 },
    { label: '150 orders', value: 150 },
    { label: '200 orders', value: 200 },
    { label: '300 orders', value: 300 },
];

/** Computed per dialog, not at module load, so a tab left open past midnight still uses today's date. */
const defaultFilters = (clientId: string | undefined) => ({
    sort: 'createdAt',
    sortDirection: 'ASC',
    limit: 200,
    createdAt_max: Dayjs(new Date()).format('YYYY-MM-DD'),
    ...(EXTRA_FEATURES_CLIENT_IDS.includes(clientId as string) ? { orderSelector: 'all' } : {}),
});

/** "Move to production": order-api switches the oldest Created orders to Production and returns a CSV of them. */
export default function OrdersProduction(props: any) {
    const { queryParams, loadOrders, loading, isPopUp, popUpHandler } = props;
    const classes = useStyles();
    const { id } = useParams();
    const [reportFilters, setReportFilters] = useState<any>(() => defaultFilters(id));
    // The list page stays mounted when the company changes: start every client with fresh defaults.
    useEffect(() => {
        setReportFilters(defaultFilters(id));
    }, [id]);
    const dataLink = useRef<HTMLAnchorElement>(null);
    const [data, setData] = useState<string | undefined>();
    const [pending, setPending] = useState(false);
    const dispatch = useDispatch();

    const handleFilterChange = (name: string, value: any) => {
        setReportFilters({ ...reportFilters, [name]: value });
    };

    const getReport = async () => {
        if (pending) return;
        setPending(true);
        try {
            await moveToProduction({
                companyId: id,
                queryParams: { ...reportFilters },
                onSuccess: (result) => {
                    setData(result.downloadUrl);
                    dispatch(Notifications.setNotifications({ text: 'Download started', type: 'success' }));
                    loadOrders(queryParams);
                    popUpHandler(true);
                },
            });
        } catch (error) {
            console.warn('[orders] move to production failed', error);
            dispatch(Notifications.setNotifications({ text: GENERIC_ERROR_MESSAGE, type: 'error' }));
        } finally {
            setPending(false);
        }
    };

    useEffect(() => {
        if (data) dataLink.current?.click();
    }, [data]);

    return (
        <>
            <a download href={data} ref={dataLink} rel="noreferrer nofollow" target="_blank" style={{ display: 'none' }} tabIndex={-1}>Download</a>
            <AppDialog
                open={Boolean(isPopUp)}
                onClose={() => popUpHandler(true)}
                title="Move orders to production"
                description='Every order in the file changes status from "Created" to "Production". The CSV downloads when the move is done.'
                pending={pending}
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => popUpHandler(true)}>Cancel</Button>
                        <Button variant="contained" onClick={getReport} disabled={pending}>Move and download CSV</Button>
                    </>
                )}
            >
                <div className={classes.form}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="production-limit-label">Orders in the file</InputLabel>
                        <Select
                            labelId="production-limit-label"
                            label="Orders in the file"
                            name="limit"
                            value={reportFilters.limit ?? ''}
                            onChange={(event) => handleFilterChange('limit', event.target.value)}
                            disabled={loading}
                        >
                            {LIMIT_OPTIONS.map((option) => (
                                <MenuItem key={option.label} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Created up to"
                            format="YYYY-MM-DD"
                            value={reportFilters.createdAt_max ? Dayjs(reportFilters.createdAt_max) : Dayjs(new Date())}
                            maxDate={Dayjs(new Date())}
                            disabled={loading}
                            onChange={(value) => { const iso = dateParam(value); if (iso) handleFilterChange('createdAt_max', iso); }}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </LocalizationProvider>
                    {EXTRA_FEATURES_CLIENT_IDS.includes(id as string) && (
                        <FormControl fullWidth size="small">
                            <InputLabel id="order-selector-label">Order selector</InputLabel>
                            <Select
                                labelId="order-selector-label"
                                label="Order selector"
                                name="orderSelector"
                                value={reportFilters.orderSelector || 'all'}
                                onChange={(event) => handleFilterChange('orderSelector', event.target.value)}
                                disabled={loading}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="xqc">XQC</MenuItem>
                                <MenuItem value="reprint">Reprint</MenuItem>
                                <MenuItem value="reprint_or_xqc">Reprint or XQC</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </div>
            </AppDialog>
        </>
    );
}
